import React from 'react';
import {
  CheckCircle2, XCircle, Stethoscope, Pencil, AlertCircle, Loader2, Plane,
} from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { formatHoursHM } from '@/lib/format';
import { REVIEW_REASON_LABELS } from './EditShiftDialog';

const DAY_LABELS = ['Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za', 'Zo'];

const STATUS_STYLES = {
  planned: 'bg-slate-100 text-slate-700 border-slate-200',
  worked: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  approved: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  rejected: 'bg-rose-100 text-rose-700 border-rose-300',
  review: 'bg-amber-100 text-amber-800 border-amber-300',
  ziek: 'bg-sky-100 text-sky-800 border-sky-300',
  vakantie: 'bg-violet-50 text-violet-800 border-violet-300',
};

const STATUS_LABEL = {
  planned: 'Gepland', worked: 'Gewerkt', approved: 'Goedgekeurd',
  rejected: 'Niet verschenen', review: 'Te beoordelen', ziek: 'Ziek', vakantie: 'Vakantie',
};

// Derive one primary status for a day cell (multiple assignments possible).
function deriveDayStatus(dayObj, dateIso, todayIso) {
  const cells = dayObj?.cells || [];
  const leave = dayObj?.leave || [];
  const hasZiek = leave.some((l) => l.type === 'ziekte');
  const hasVak = leave.some((l) => l.type === 'vakantie');
  if (cells.length === 0) {
    if (hasZiek) return 'ziek';
    if (hasVak) return 'vakantie';
    return null;
  }
  if (hasZiek) return 'ziek';
  if (cells.some((c) => c.status === 'rejected')) return 'rejected';
  if (cells.some((c) => (c.status === 'planned' && dateIso < todayIso) || c.time_record_needs_review)) return 'review';
  if (cells.every((c) => c.status === 'approved')) return 'approved';
  if (cells.some((c) => c.status === 'worked')) return 'worked';
  return 'planned';
}

const LEGEND = [
  ['approved', 'Goedgekeurd'], ['worked', 'Gewerkt'], ['planned', 'Gepland'],
  ['review', 'Te beoordelen'], ['rejected', 'Niet verschenen'],
  ['ziek', 'Ziek'], ['vakantie', 'Vakantie/verlof'],
];

export function MatrixLegend() {
  return (
    <div className="flex flex-wrap items-center gap-3 px-1 py-2 text-[11px] text-slate-500" data-testid="matrix-legend">
      <span className="font-medium text-slate-600">Legenda</span>
      {LEGEND.map(([key, label]) => (
        <span key={key} className="flex items-center gap-1.5" data-testid={`legend-${key}`}>
          <span className={`inline-block h-3 w-3 rounded border ${STATUS_STYLES[key]}`} />
          {label}
        </span>
      ))}
    </div>
  );
}

function initials(name) {
  return name?.split(' ').slice(0, 2).map((s) => s[0]).join('').toUpperCase() || '?';
}

function CellPopover({ emp, dateIso, dayObj, todayIso, onApprove, onEditCell, actionPending }) {
  const cells = dayObj?.cells || [];
  const leave = dayObj?.leave || [];
  const status = deriveDayStatus(dayObj, dateIso, todayIso);
  const totalNet = cells.reduce((s, c) => s + (c.duration_net || 0), 0);
  const empty = cells.length === 0 && leave.length === 0;

  const trigger = (
    <button
      type="button"
      data-testid={`matrix-cell-${emp.personeelsnummer}-${dateIso}`}
      disabled={empty}
      className={`flex h-full min-h-[46px] w-full flex-col items-center justify-center rounded-md border px-1 py-1 text-[11px] leading-tight transition-colors ${
        empty
          ? 'cursor-default border-dashed border-slate-100 bg-slate-50/40 text-slate-300'
          : `${STATUS_STYLES[status] || STATUS_STYLES.planned} hover:brightness-95`
      }`}
    >
      {empty ? (
        <span>—</span>
      ) : (
        <>
          <span className="font-semibold tabular-nums">
            {cells.length > 0 ? formatHoursHM(totalNet) : STATUS_LABEL[status]}
          </span>
          {status === 'review' && <AlertCircle size={11} className="mt-0.5" />}
          {status === 'ziek' && cells.length === 0 && <Stethoscope size={11} className="mt-0.5" />}
          {status === 'vakantie' && cells.length === 0 && <Plane size={11} className="mt-0.5" />}
        </>
      )}
    </button>
  );

  if (empty) return trigger;

  return (
    <Popover>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent align="center" className="w-72 p-3" data-testid={`matrix-popover-${emp.personeelsnummer}-${dateIso}`}>
        <div className="mb-2">
          <p className="text-sm font-semibold text-slate-900">{emp.name}</p>
          <p className="text-[11px] text-slate-500">#{emp.personeelsnummer} · {dateIso}</p>
        </div>
        {leave.length > 0 && (
          <div className="mb-2 flex flex-wrap gap-1">
            {leave.map((l, i) => (
              <Badge key={i} variant="outline" className={STATUS_STYLES[l.type === 'ziekte' ? 'ziek' : 'vakantie']}>
                {l.type === 'ziekte' ? 'Ziek' : 'Vakantie'} {l.hours ? formatHoursHM(l.hours) : ''}
              </Badge>
            ))}
          </div>
        )}
        <div className="space-y-2">
          {cells.map((c) => {
            const busy = actionPending && actionPending.endsWith(c.assignment_id);
            return (
              <div key={c.assignment_id} className="rounded-md border border-slate-200 p-2">
                <div className="mb-1.5 flex items-center justify-between text-[12px]">
                  <span className="tabular-nums text-slate-700">{c.start_time}–{c.end_time}</span>
                  <span className="tabular-nums font-medium">{formatHoursHM(c.duration_net)}</span>
                </div>
                {c.actual_clock_in && (
                  <p className="mb-1.5 text-[11px] text-slate-500">
                    Ingeklokt: <span className="tabular-nums">{c.actual_clock_in}–{c.actual_clock_out || 'nu'}</span>
                  </p>
                )}
                {c.time_record_needs_review && (
                  <p className="mb-1.5 text-[11px] font-medium text-rose-600">
                    {REVIEW_REASON_LABELS[c.time_record_review_reason] || 'Klok-review nodig'}
                  </p>
                )}
                <div className="flex flex-wrap gap-1">
                  <Button
                    size="sm" variant="outline"
                    data-testid={`matrix-edit-${c.assignment_id}`}
                    onClick={() => onEditCell(c, emp)}
                    disabled={!!actionPending}
                    className="h-6 px-1.5 text-[10px]"
                  >
                    <Pencil size={11} className="mr-0.5" /> Bewerk
                  </Button>
                  <Button
                    size="sm" variant="outline"
                    data-testid={`matrix-approve-${c.assignment_id}`}
                    onClick={() => onApprove([c.assignment_id], 'approved', null)}
                    disabled={!!actionPending}
                    className="h-6 px-1.5 text-[10px]"
                  >
                    {busy ? <Loader2 size={11} className="mr-0.5 animate-spin" /> : <CheckCircle2 size={11} className="mr-0.5" />} Gewerkt
                  </Button>
                  <Button
                    size="sm" variant="outline"
                    data-testid={`matrix-sick-${c.assignment_id}`}
                    onClick={() => onApprove([c.assignment_id], 'approved', 'ziek')}
                    disabled={!!actionPending}
                    className="h-6 px-1.5 text-[10px] border-amber-300 text-amber-700 hover:bg-amber-50"
                  >
                    <Stethoscope size={11} className="mr-0.5" /> Ziek
                  </Button>
                  <Button
                    size="sm" variant="outline"
                    data-testid={`matrix-noshow-${c.assignment_id}`}
                    onClick={() => onApprove([c.assignment_id], 'rejected', 'niet verschenen')}
                    disabled={!!actionPending}
                    className="h-6 px-1.5 text-[10px] border-rose-300 text-rose-700 hover:bg-rose-50"
                  >
                    <XCircle size={11} className="mr-0.5" /> Niet versch.
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function WeekMatrix({ weekDates, employees, groups, todayIso, onApprove, onEditCell, actionPending }) {
  const dateIsos = weekDates.map((d) => (d ? d.toISOString().slice(0, 10) : null));

  if (!employees || employees.length === 0) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-10 text-center" data-testid="matrix-empty">
        <AlertCircle size={30} className="mx-auto mb-2 text-slate-300" />
        <p className="text-sm text-slate-500">Geen shifts voor deze week.</p>
      </div>
    );
  }

  const renderRow = (emp) => (
    <tr
      key={`${emp.location_id || ''}-${emp.employee_id}`}
      className="border-t border-slate-100"
      data-testid={`matrix-row-${emp.personeelsnummer}`}
    >
      <td className="sticky left-0 z-10 bg-white px-3 py-2">
        <div className="flex items-center gap-2">
          <div className="flex h-7 w-7 items-center justify-center rounded-full bg-brand-navy/10 text-[10px] font-semibold text-brand-navy">
            {initials(emp.name)}
          </div>
          <div className="leading-tight">
            <p className="text-[13px] font-medium text-slate-900">{emp.name}</p>
            <p className="text-[10px] text-slate-500">
              #{emp.personeelsnummer} · {formatHoursHM(emp.planned_hours)} gepland
              {emp.needs_review && <span className="ml-1 text-amber-600">· review</span>}
            </p>
          </div>
        </div>
      </td>
      {dateIsos.map((iso, i) => (
        <td key={i} className="p-1 align-middle">
          {iso && (
            <CellPopover
              emp={emp}
              dateIso={iso}
              dayObj={emp.days?.[iso]}
              todayIso={todayIso}
              onApprove={onApprove}
              onEditCell={onEditCell}
              actionPending={actionPending}
            />
          )}
        </td>
      ))}
    </tr>
  );

  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm" data-testid="week-matrix">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="bg-slate-50">
            <th className="sticky left-0 z-10 min-w-[210px] bg-slate-50 px-3 py-2 text-left text-xs font-semibold text-slate-600">
              Medewerker
            </th>
            {dateIsos.map((iso, i) => (
              <th key={i} className="min-w-[92px] px-1.5 py-2 text-center text-xs font-semibold text-slate-600">
                <div>{DAY_LABELS[i]}</div>
                <div className="text-[10px] font-normal text-slate-400">{iso ? iso.slice(8) + '-' + iso.slice(5, 7) : ''}</div>
              </th>
            ))}
          </tr>
        </thead>
        {groups && groups.length > 0 ? (
          groups.map((g) => (
            <tbody key={g.location_id} data-testid={`matrix-group-${g.location_id}`}>
              <tr className="border-t border-slate-200 bg-brand-navy/5">
                <th
                  colSpan={dateIsos.length + 1}
                  className="px-3 py-1.5 text-left text-[11px] font-semibold uppercase tracking-wide text-brand-navy"
                >
                  {g.location_name}
                  <span className="ml-2 font-normal normal-case text-slate-500">
                    {g.count} medewerker{g.count === 1 ? '' : 's'}
                    {g.approvable_count > 0 && ` · ${g.approvable_count} te beoordelen`}
                  </span>
                </th>
              </tr>
              {g.employees.map(renderRow)}
            </tbody>
          ))
        ) : (
          <tbody>{employees.map(renderRow)}</tbody>
        )}
      </table>
    </div>
  );
}
